import hash from 'object-hash';
import { commitMutation, graphql } from 'react-relay';
import { createAtlAttributionHeader } from '../../utils';
import {
	type SubscribeRealtimeChannelPayload as SubscribeRealtimeChannelPayloadV1,
	getSubscribePayload,
} from '../../web-client/hooks/useSubscribeRealtimeChannel';
import { type PublishRealtimeChannelPayload as PublishRealtimeChannelPayloadV1 } from '../../web-client/hooks/usePublishRealtimeChannel';
import { subscribe } from '../../web-client/hooks/lib/aggSubscription';
import { validateRealtimeChannelToken } from '../../web-client/hooks/lib/realtimeToken';
import { createBridgeResource, type BridgeResource } from '../bridge-resource';
import { type MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import type { realtimePublishChannelMutation } from './__generated__/realtimePublishChannelMutation.graphql';
import { fg } from '@atlaskit/platform-feature-flags';
import type { AGGPayloadError } from '../../web-client/hooks/lib/realtime';

// In v2, convertToBridgeInvokeHandler already unwraps the outer `{ data }` before
// calling a TBridgeMethod, so these methods receive a flat payload — the inner `data`
// shape of the v1 payload types.
type SubscribeRealtimeChannelPayload = SubscribeRealtimeChannelPayloadV1['data'];
type PublishRealtimeChannelPayload = PublishRealtimeChannelPayloadV1['data'];

type SubscribeRealtimeChannelContext = MakeBridgeContext<
	'extension' | 'claimBridgeResource' | 'resolverContext',
	'getContextToken'
>;

type PublishRealtimeChannelContext = MakeBridgeContext<
	'extension' | 'resolverContext',
	'getContextToken' | 'relayEnvironment'
>;

type SubscriptionResult = {
	data: {
		ecosystem: {
			realtimeChannel?: {
				payload?: string;
			};
		};
	};
};

type RealtimeEventResult = {
	errors?: any;
	eventId: string | null;
	eventTimestamp: string | null;
};

type SubscriptionHandle = {
	unsubscribe: () => void;
};

type RealtimeSubscriptionEntry = {
	subscription: ReturnType<typeof subscribe>;
};

type RealtimeSharedState = {
	activeSubscriptions: Map<string, RealtimeSubscriptionEntry>;
};

/** @visibleForTesting */
export const realtimeResource: BridgeResource<RealtimeSharedState> =
	createBridgeResource<RealtimeSharedState>(
		'realtime',
		() => ({ activeSubscriptions: new Map() }),
		(state) => {
			for (const { subscription } of state.activeSubscriptions.values()) {
				if (!subscription.closed) {
					subscription.unsubscribe();
				}
			}
			state.activeSubscriptions.clear();
		},
	);

const getChannelKey = (context: {
	channelContext?: string;
	channelName: string;
	isGlobal?: boolean;
}) => {
	return Object.entries(context)
		.sort((a, b) => a[0].localeCompare(b[0]))
		.toString();
};

/**
 * Computes the extension context hash used to scope realtime channels.
 * Matches the hash computed by the backend Realtime service and the legacy InnerIframe.tsx.
 */
const computeExtensionContext = (resolverContext: {
	extension?: Record<string, unknown>;
}): string => {
	try {
		const { modal: _modal, ...productExtensionData } = resolverContext.extension ?? {};
		return hash(productExtensionData);
	} catch {
		// eslint-disable-next-line no-console
		console.error('Product context is not a serializable object.');
		return '';
	}
};

/**
 * Subscribes the iframe client to a realtime channel and deduplicates active subscriptions by channel key.
 *
 * If a subscription for the same channel key already exists, this method adds the callback to the existing subscription and emits SUBSCRIPTION_STARTED.
 * On Unauthorized request errors, the method refreshes the context token, re-subscribes, and preserves existing callbacks on the new subscription.
 */
export const subscribeRealtimeChannel: TBridgeMethod<
	SubscribeRealtimeChannelContext,
	SubscribeRealtimeChannelPayload,
	SubscriptionHandle
> = async (context, payload) => {
	const {
		extension,
		claimBridgeResource,
		resolverContext,
		services: { getContextToken },
	} = context;
	const realtimeState = claimBridgeResource(realtimeResource);
	const { onEvent, channelName, options, isGlobal } = payload;
	const { token } = options || {};

	const prevalidate =
		fg('forge-ui-realtime-add-pre-permissions-validation') ||
		fg('forge-ui-realtime-pre-validate-frt-existing-fields');

	if (token && prevalidate) {
		const validation = validateRealtimeChannelToken(token, {
			requiredPermission: 'subscribe',
			channelName,
		});

		if (!validation.valid) {
			return Promise.reject(new Error(`Realtime token validation failed: ${validation.error}`));
		}
	}

	const extensionContext = computeExtensionContext(resolverContext);
	const channelContext = options?.contextOverrides
		? JSON.stringify({
				context: extensionContext,
				contextOverrides: options?.contextOverrides,
			})
		: extensionContext;

	const channelKey = getChannelKey({
		channelName,
		isGlobal,
		channelContext,
	});

	const existingEntry = realtimeState.activeSubscriptions.get(channelKey);

	if (existingEntry) {
		existingEntry.subscription.addCallback(onEvent);
		onEvent('SUBSCRIPTION_STARTED');
	} else {
		const createSubscription = async () => {
			const subscriptionPayload = getSubscribePayload({
				// getSubscribePayload expects the v1-shaped { data: {...} } envelope.
				payload: { data: payload },
				context: channelContext,
				installationId: extension.installationId,
				contextToken: await getContextToken(),
			});

			// Declare before subscribe() so the sink closures can reference it.
			// Assignment happens synchronously before any sink callbacks fire.
			let subscription!: ReturnType<typeof subscribe>;

			subscription = subscribe(
				subscriptionPayload,
				{
					next: (result: SubscriptionResult) => {
						subscription.onNext(result.data.ecosystem?.realtimeChannel?.payload);
					},
					error: async (err: unknown) => {
						const error = Array.isArray(err) ? err[0] : err;

						if (
							error?.extensions?.statusCode === 429 &&
							fg('forge-ui-handle-realtime-rate-limit-error')
						) {
							subscription.onError(new Error('RATE_LIMIT_EXCEEDED'));
							return;
						}

						const shouldRetrySubscribe = fg('forge-ui-retry-subscribe-only-for-expired-fct')
							? error?.message === 'FCT has expired on subscribe request'
							: error?.message === 'Unauthorized request' ||
								error?.message === 'FCT has expired on subscribe request';

						if (shouldRetrySubscribe) {
							try {
								const callbacksToCopy = [...subscription.subscriptionCallbacks];
								await createSubscription();
								const newEntry = realtimeState.activeSubscriptions.get(channelKey);
								if (newEntry) {
									newEntry.subscription.subscriptionCallbacks = callbacksToCopy;
								}
								subscription.unsubscribe();
								// The new subscription is already at channelKey — don't delete the map entry.
								return;
							} catch (retryError) {
								subscription.onError(retryError as Error);
								if (
									realtimeState.activeSubscriptions.get(channelKey)?.subscription === subscription
								) {
									realtimeState.activeSubscriptions.delete(channelKey);
								}
								if (!subscription.closed) {
									subscription.unsubscribe();
								}
								return;
							}
						}

						subscription.onError(error as Error);
						if (realtimeState.activeSubscriptions.get(channelKey)?.subscription === subscription) {
							realtimeState.activeSubscriptions.delete(channelKey);
						}
						if (!subscription.closed) {
							subscription.unsubscribe();
						}
					},
					complete: () => {
						if (realtimeState.activeSubscriptions.get(channelKey)?.subscription === subscription) {
							realtimeState.activeSubscriptions.delete(channelKey);
						}
						if (!subscription.closed) {
							subscription.unsubscribe();
						}
					},
				},
				onEvent,
			);

			realtimeState.activeSubscriptions.set(channelKey, { subscription });
			return subscription;
		};

		void createSubscription();
	}

	return {
		unsubscribe: () => {
			const entry = realtimeState.activeSubscriptions.get(channelKey);
			if (entry) {
				entry.subscription.removeCallback(onEvent);
				if (entry.subscription.subscriptionCallbacks.length === 0) {
					realtimeState.activeSubscriptions.delete(channelKey);
					if (!entry.subscription.closed) {
						entry.subscription.unsubscribe();
					}
				}
			}
		},
	};
};

/**
 * Publishes an event payload to a realtime channel scoped by installation and extension context.
 *
 * The payload is serialized as JSON and sent via Relay mutation with the current Forge context token header when available.
 * If the mutation succeeds without a complete eventId/eventTimestamp pair, this method resolves to undefined.
 */
export const publishRealtimeChannel: TBridgeMethod<
	PublishRealtimeChannelContext,
	PublishRealtimeChannelPayload,
	RealtimeEventResult | undefined
> = async (context, payload) => {
	const {
		extension,
		resolverContext,
		services: { getContextToken, relayEnvironment },
	} = context;

	const extensionContext = computeExtensionContext(resolverContext);
	const contextToken = await getContextToken();

	const { channelName, isGlobal, options } = payload;
	const { token, contextOverrides } = options || {};

	const prevalidate =
		fg('forge-ui-realtime-add-pre-permissions-validation') ||
		fg('forge-ui-realtime-pre-validate-frt-existing-fields');

	if (token && prevalidate) {
		const validation = validateRealtimeChannelToken(token, {
			requiredPermission: 'publish',
			channelName,
		});

		if (!validation.valid) {
			return {
				eventId: null,
				eventTimestamp: null,
				errors: [
					{
						message: `Realtime token validation failed: ${validation.error}`,
					},
				],
			};
		}
	}

	const eventPayload = JSON.stringify(payload.eventPayload);
	const channelContext = contextOverrides
		? JSON.stringify({
				context: extensionContext,
				contextOverrides,
			})
		: extensionContext;

	return new Promise<RealtimeEventResult | undefined>((resolve, reject) => {
		commitMutation<realtimePublishChannelMutation>(relayEnvironment, {
			mutation: graphql`
				mutation realtimePublishChannelMutation(
					$installationId: ID!
					$name: String!
					$payload: String!
					$context: String
					$isGlobal: Boolean
					$token: String
				) {
					ecosystem {
						publishRealtimeChannel(
							installationId: $installationId
							name: $name
							payload: $payload
							context: $context
							isGlobal: $isGlobal
							token: $token
						) {
							eventId
							eventTimestamp
						}
					}
				}
			`,
			variables: {
				installationId: extension.installationId,
				name: channelName,
				payload: eventPayload,
				context: channelContext,
				isGlobal: isGlobal ?? false,
				token,
			},
			cacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
						...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
						'x-rate-limit-app-id': extension.appId,
						'x-rate-limit-installation-id': extension.installationId,
					},
				},
			},
			onCompleted: (data, errors) => {
				if (errors) {
					if (fg('forge-ui-realtime-fix-error-handling-publish')) {
						return resolve({
							eventId: null,
							eventTimestamp: null,
							errors: fg('forge-ui-handle-realtime-rate-limit-error')
								? errors.map((error: AGGPayloadError) => {
										if (error.extensions?.statusCode === 429) {
											return { message: 'RATE_LIMIT_EXCEEDED' };
										}
										return error;
									})
								: errors,
						});
					} else {
						reject(errors);
					}
					return;
				}

				if (data?.ecosystem?.publishRealtimeChannel) {
					const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;
					if (eventId && eventTimestamp) {
						resolve({ eventId, eventTimestamp });
						return;
					}
				}

				resolve(undefined);
			},
			onError: reject,
		});
	});
};
